import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProducts } from '../../slices/ProductSlice';
import Item from '../item/Item.jsx';
import { Link } from 'react-router-dom';

function Feature() {
  const products = useSelector(state => state.products.products);
  const loading = useSelector(state => state.products.loading);
  const error = useSelector(state => state.products.error);
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Logic: Get last 3 items (Newest)
  const featuredProducts = [...products].slice(-3).reverse();

  useEffect(() => {
    if (products.length === 0 && !loading && !error) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products.length, loading, error]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Mobile Auto-slide
  useEffect(() => {
    if (featuredProducts.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex >= featuredProducts.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [featuredProducts.length]);

  const nextSlide = () => {
    setCurrentIndex(currentIndex >= featuredProducts.length - 1 ? 0 : currentIndex + 1);
  };

  const prevSlide = () => {
    setCurrentIndex(currentIndex <= 0 ? featuredProducts.length - 1 : currentIndex - 1);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section
      id='featured-page'
      className='relative w-full py-24 md:py-32 lg:py-40 bg-white overflow-hidden'
    >
      {/* ✅ FIXED PADDING: Matches Navbar & Hero exactly (px-4 md:px-8 lg:px-12) */}
      <div className='max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 relative z-10'>

        {/* --- LOADING --- */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32">
            <div className="w-8 h-8 border border-gray-100 border-t-black rounded-full animate-spin duration-700"></div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-6 animate-pulse">Loading Selection</p>
          </div>
        )}

        {/* --- ERROR --- */}
        {error && (
          <div className="text-center py-32">
            <p className="text-sm font-serif italic text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => dispatch(fetchProducts())}
              className="text-[10px] uppercase tracking-[0.2em] border-b border-black pb-1 hover:opacity-50 transition-opacity"
            >
              Try Again
            </button>
          </div>
        )}

        {/* --- EMPTY --- */}
        {!loading && !error && featuredProducts.length === 0 && (
          <div className="text-center py-32">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">Collection Updating</p>
          </div>
        )}

        {/* --- CONTENT --- */}
        {!loading && !error && featuredProducts.length > 0 && (
          <>
            {/* 1. HEADER */}
            <div className={`text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span className="block text-[10px] md:text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">
                Curated Selection
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl text-black font-serif italic">
                Most Wanted
              </h2>
            </div>

            {/* 2. MOBILE SLIDER */}
            <div className="block md:hidden mb-16">
              <div className="relative">
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-700 cubic-bezier(0.4, 0, 0.2, 1)"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                  >
                    {featuredProducts.map((product) => (
                      <div key={product.id} className="w-full flex-shrink-0 px-4">
                        <Item 
                          id={product.id}
                          image={product.imageUrl}
                          name={product.name}
                          price={product.price}
                          stock={product.stock}
                          hasDiscount={product.hasDiscount}
                          discountedPrice={product.discountedPrice}
                          discountBadge={product.discount?.badge}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Minimal Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 p-2 hover:opacity-50 transition-opacity"
                >
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 hover:opacity-50 transition-opacity"
                >
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>

              {/* Dots */}
              <div className="flex justify-center mt-8 gap-3">
                {featuredProducts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`h-[2px] transition-all duration-500 ${
                      index === currentIndex ? 'bg-black w-8' : 'bg-gray-200 w-4'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* 3. DESKTOP GRID */}
            <div className="hidden md:grid grid-cols-3 gap-x-8 lg:gap-x-12 mb-24">
              {featuredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className={`transform transition-all duration-1000 ease-out`}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                    transitionDelay: `${index * 150}ms`,
                  }}
                >
                  <Item 
                    id={product.id}
                    image={product.imageUrl}
                    name={product.name}
                    price={product.price}
                    stock={product.stock}
                    hasDiscount={product.hasDiscount}
                    discountedPrice={product.discountedPrice}
                    discountBadge={product.discount?.badge}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* 4. FOOTER CTA */}
        {!loading && !error && featuredProducts.length > 0 && (
          <div className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
             <Link to="/shop">
               <button className="group relative px-12 py-4 bg-transparent border border-black/10 overflow-hidden transition-colors hover:border-black">
                 <span className="absolute inset-0 w-full h-full bg-black transform -translate-x-full transition-transform duration-500 ease-out group-hover:translate-x-0"></span>
                 <span className="relative z-10 flex items-center gap-4 text-[11px] uppercase tracking-[0.2em] font-medium text-black group-hover:text-white transition-colors duration-300">
                   View Full Collection
                 </span>
               </button>
             </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default Feature;